/**
 * global.js - La Maison des Housses
 *
 * Socle charge sur toutes les pages. Il reste volontairement court : le
 * budget INP est de 200 ms, et tout le contenu indexable (FAQ, accordeons,
 * filtres) est deja dans le HTML servi par Liquid. Le JavaScript ne fait
 * qu'ajouter du confort par-dessus.
 */

(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const LMH = (window.LMH = window.LMH || {});

  /* ----------------------------------------------------------------------
   * Utilitaires partages
   * -------------------------------------------------------------------- */

  const euros = new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  });

  /** Formate des centimes Shopify au format francais : 34,90 €. */
  LMH.formatMoney = (cents) => euros.format((Number(cents) || 0) / 100);

  /** lmdh 28/09/2026 (320) : montant sans « ,00 » inutile (85 €, mais 12,30 €), comme money_without_trailing_zeros. */
  const eurosEntiers = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
  LMH.formatMoneyCourt = (cents) => {
    const c = Number(cents) || 0;
    return c % 100 === 0 ? eurosEntiers.format(c / 100) : euros.format(c / 100);
  };

  /**
   * lmdh 28/09/2026 : le moment « 85 € atteint ». La jauge finit sa course depuis sa largeur d'avant,
   * passe au vert, la coche apparait. Rien si le visiteur a demande moins d'animations.
   */
  LMH.celebrerJauge = (gauge, largeurAvant) => {
    if (!gauge || reduceMotion.matches) return;
    if (largeurAvant) gauge.style.setProperty('--gauge-from', largeurAvant);
    gauge.classList.remove('gauge--celebre');
    void gauge.offsetWidth;
    gauge.classList.add('gauge--celebre');
    LMH.celebreJusqua = Date.now() + 1400;
    clearTimeout(gauge.lmdhCelebre);
    gauge.lmdhCelebre = setTimeout(() => gauge.classList.remove('gauge--celebre'), 1400);
  };

  /**
   * Recupere une ou plusieurs sections rendues par le serveur.
   * On prefere toujours cette voie a une reconstruction cote client :
   * les prix, remises et traductions restent calcules par Shopify.
   */
  LMH.fetchSections = async (sectionIds, url = window.location.pathname) => {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('sections', sectionIds.join(','));

    const response = await fetch(target, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Section rendering failed: ${response.status}`);
    return response.json();
  };

  /** Remplace le contenu d'un element par celui du meme selecteur dans du HTML brut. */
  LMH.replaceFrom = (html, selector) => {
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    const destination = document.querySelector(selector);
    if (!source || !destination) return false;
    destination.innerHTML = source.innerHTML;
    return true;
  };

  /* lmdh 30/09/2026 (rapport 334, C2 et B10) : familles de coloris, UNE table pour tout le theme (filtre des
     collections, pastilles de la fiche). La famille se lit dans le PREMIER MOT du nom (« Gris palmes » -> Gris) ;
     un premier mot absent de la table ne range le coloris nulle part, jamais une famille par defaut. Controle du
     30/09 sur les 291 coloris des 48 heros : 281 ranges, 10 motifs sans couleur (Geometrique, Patchwork, Triangles). */
  LMH.FAMILLES = [{"cle":"gris","nom":"Gris","couleur":"#8B8E90","mots":["gris","anthracite","antracita"]},{"cle":"noir","nom":"Negro","couleur":"#1F1F1F","mots":["noir","ébène","negro","ébano"]},{"cle":"blanc","nom":"Blanco","couleur":"#F6F3EC","mots":["blanc","blanco"]},{"cle":"beige","nom":"Beige y crudo","couleur":"#D8C6A5","mots":["beige","écru","sable","grège","ivoire","champagne","taupe","arena","marfil","crudo","topo","champán"]},{"cle":"marron","nom":"Marrón y camel","couleur":"#7A5234","mots":["marron","brun","camel","caramel","café","cacao","chocolat","moka","cognac","noisette","acajou","marrón","chocolate","moca","caoba","avellana","coñac","caramelo"]},{"cle":"bleu","nom":"Azul","couleur":"#2F4F7F","mots":["bleu","marine","turquoise","azul","turquesa","marino"]},{"cle":"vert","nom":"Verde","couleur":"#4F7A4A","mots":["vert","olive","kaki","forêt","verde","caqui","oliva"]},{"cle":"rouge","nom":"Rojo y burdeos","couleur":"#8E1F2C","mots":["rouge","bordeaux","carmin","grenat","rubis","lie","pourpre","burdeos","rojo","púrpura","carmesí","rubí","vino","granate"]},{"cle":"rose","nom":"Rosa","couleur":"#E2A3B4","mots":["rose","magenta","rosa"]},{"cle":"violet","nom":"Violeta y ciruela","couleur":"#6B3B5E","mots":["violet","mauve","lilas","lavande","parme","prune","aubergine","ciruela","berenjena"]},{"cle":"jaune","nom":"Amarillo y naranja","couleur":"#E3A33B","mots":["jaune","orange","mandarine","terracotta","miel","ambre","amarillo","naranja","ámbar","terracota","mandarina"]},{"cle":"multicolore","nom":"Multicolor","couleur":"conic-gradient(#C0563B 0 25%, #2F4F7F 0 50%, #E3A33B 0 75%, #4F7A4A 0)","mots":["multicolore","patchwork","géométrique","triangles","geométrico","triángulos"]}];
  // lmdh 30/09/2026 (retours du proprietaire) : « Violet » (10 collections) n avait pas de famille ; les motifs sans
  // couleur dominante (geometriques, patchworks, triangles) vont dans « Multicolore » au lieu de sortir du filtre.
  LMH.familleColoris = (nom) => {
    const mot = String(nom || '').trim().split(/[\s-]/)[0].toLowerCase();
    return LMH.FAMILLES.find((f) => f.mots.includes(mot)) || null;
  };

  LMH.toast = (message, variant = 'success') => {
    const region = document.getElementById('toast-region');
    if (!region) return;

    // lmdh 30/09/2026 (rapport 334, D2) : un <dialog> modal passe au-dessus de tout ; la zone de messages
    // restee dans <body> etait cachee par le tiroir et inerte pour les lecteurs d ecran. Le temps du
    // message, elle loge dans le panneau ouvert.
    const ouvert = [...document.querySelectorAll('dialog[open]')].pop();
    const hote = ouvert || document.body;
    if (region.parentElement !== hote) hote.appendChild(region);

    region.innerHTML = '';
    const toast = document.createElement('p');
    toast.className = 'toast';
    toast.dataset.variant = variant;
    toast.textContent = message;
    region.appendChild(toast);

    window.clearTimeout(Number(region.dataset.timer));
    region.dataset.timer = String(window.setTimeout(() => toast.remove(), 4000));
  };

  /** Diffuse l'etat du panier a tous les composants interesses. */
  LMH.publishCart = (cart) => {
    if (!cart) return;
    LMH.cart = cart;
    document.dispatchEvent(new CustomEvent('lmh:cart:updated', { detail: { cart } }));
  };

  LMH.refreshCart = async () => {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;
    const cart = await response.json();
    LMH.publishCart(cart);
    return cart;
  };

  /* ----------------------------------------------------------------------
   * Tiroirs et modales
   *
   * <dialog> natif : piege a focus, touche Echap et fond assombri sont geres
   * par le navigateur. On n'ajoute que l'ouverture declarative, la fermeture
   * animee et le verrouillage du defilement de la page.
   * -------------------------------------------------------------------- */

  // lmdh 29/09/2026 : a l ouverture d un panneau, le navigateur pose le focus sur son premier element
  // (logo du menu, croix du choix des housses offertes) et Safari sur iPhone y dessine le cadre de focus,
  // meme apres un toucher. On note le dernier mode de saisie sur <html data-input> : au doigt ou a la
  // souris, ce cadre est masque dans les panneaux (critical.css) ; a la touche Tab ou aux fleches, il revient.
  const setInputMode = (mode) => {
    if (document.documentElement.dataset.input !== mode) document.documentElement.dataset.input = mode;
  };
  document.addEventListener('pointerdown', () => setInputMode('pointer'), { capture: true, passive: true });
  document.addEventListener(
    'keydown',
    (event) => {
      const key = String(event.key || '');
      if (key === 'Tab' || key.startsWith('Arrow')) setInputMode('keyboard');
    },
    true
  );

  const lockScroll = (locked) => {
    document.documentElement.classList.toggle('is-scroll-locked', locked);
  };

  const anyDialogOpen = () => document.querySelector('dialog[open]') !== null;

  const closeDialog = (dialog) => {
    if (!dialog || !dialog.open) return;

    const finish = () => {
      dialog.removeAttribute('data-closing');
      dialog.close();
      lockScroll(anyDialogOpen());
      syncDialogTriggers(dialog, false);
      // lmdh 28/09/2026 : retour du focus ici aussi, sans attendre l evenement « close »
      const back = dialog._returnFocus;
      dialog._returnFocus = null;
      if (back) focusWhenReady(back);
    };

    if (reduceMotion.matches) {
      finish();
      return;
    }

    dialog.setAttribute('data-closing', '');
    dialog.addEventListener('animationend', finish, { once: true });
    window.setTimeout(finish, 400);
  };

  const syncDialogTriggers = (dialog, open) => {
    if (!dialog?.id) return;
    document.querySelectorAll(`[data-dialog-open="${dialog.id}"]`).forEach((trigger) => {
      trigger.setAttribute('aria-expanded', String(open));
    });
  };

  LMH.openDialog = (id, options = {}) => {
    const dialog = document.getElementById(id);
    if (!dialog || typeof dialog.showModal !== 'function') return null;
    if (!dialog.open) {
      // lmdh 28/09/2026 : a la fermeture, le focus revient a l element d origine. Le bouton d achat
      // est desactive pendant l ajout : sans element fourni, il retombait sur <body>.
      const active = document.activeElement;
      dialog._returnFocus = options.returnFocus || (active && active !== document.body ? active : null);
      dialog.showModal();
      lockScroll(true);
      syncDialogTriggers(dialog, true);
      dialog.dispatchEvent(new CustomEvent('lmh:dialog:opened'));
    }
    return dialog;
  };

  LMH.closeDialog = (id) => closeDialog(document.getElementById(id));

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      event.preventDefault();
      LMH.openDialog(opener.dataset.dialogOpen);
      return;
    }

    const closer = event.target.closest('[data-dialog-close]');
    if (closer) {
      event.preventDefault();
      closeDialog(closer.closest('dialog'));
    }
  });

  // Un clic hors du panneau ferme le dialogue. On compare aux limites du
  // panneau plutot qu'a la cible : le fond assombri appartient au <dialog>.
  document.addEventListener('mousedown', (event) => {
    if (event.target.tagName !== 'DIALOG' || !event.target.open) return;
    const box = event.target.getBoundingClientRect();
    const inside =
      event.clientX >= box.left &&
      event.clientX <= box.right &&
      event.clientY >= box.top &&
      event.clientY <= box.bottom;
    if (!inside) closeDialog(event.target);
  });

  // lmdh 28/09/2026 : « cancel » (touche Echap) ne remonte pas jusqu au document. Ecoute en capture,
  // sinon le dialogue se fermait sans deverrouiller la page (html.is-scroll-locked restait pose).
  document.addEventListener(
    'cancel',
    (event) => {
      if (event.target.tagName !== 'DIALOG') return;
      event.preventDefault();
      closeDialog(event.target);
    },
    true
  );

  const focusWhenReady = (element, tries = 0) => {
    if (!element || !document.contains(element)) return;
    if (!element.disabled) {
      element.focus({ preventScroll: true });
      return;
    }
    if (tries < 30) window.setTimeout(() => focusWhenReady(element, tries + 1), 100);
  };

  // Filet : quelle que soit la facon dont un dialogue se ferme, la page, les declencheurs et le focus suivent.
  document.addEventListener(
    'close',
    (event) => {
      const dialog = event.target;
      if (dialog.tagName !== 'DIALOG') return;
      lockScroll(anyDialogOpen());
      syncDialogTriggers(dialog, false);
      const back = dialog._returnFocus;
      dialog._returnFocus = null;
      if (back) focusWhenReady(back);
    },
    true
  );

  /* ----------------------------------------------------------------------
   * En-tete
   *
   * Compaction au defilement (logo et paddings reduits) et menu deroulant
   * au survol avec pont de survol : le panneau reste ouvert pendant que la
   * souris traverse l'espace vide sous l'onglet.
   * -------------------------------------------------------------------- */

  const setupHeader = () => {
    const header = document.querySelector('[data-header]');
    if (!header) return;

    // Publiee en variable CSS pour que les elements collants (barre laterale
    // des filtres, galerie produit) se calent sous le header sans redeclarer
    // sa hauteur chacun de leur cote.
    // Le header se compacte en animant sa hauteur : pendant ces 300 ms, le
    // ResizeObserver se declenche a chaque frame. Sans garde, chaque frame
    // reecrirait --header-height sur <html> et invaliderait la position de
    // tous les elements collants qui en dependent. On ne publie donc qu'une
    // fois par frame, et seulement si la valeur arrondie a reellement change.
    let publishedHeight = null;
    let publishPending = false;
    // lmdh 12/10/2026 (lot AS, B1 de l audit technique) : au telephone (moins de 990 px), pendant la compaction (300 ms),
    // la hauteur n est publiee qu une fois, a la fin : chaque publication sur <html> recalculait le style de toute la page
    // (90 ms environ par image au telephone ralenti, trois ou quatre fois de suite). Sur ordinateur, rien ne change :
    // les elements collants y suivent l en-tete image par image.
    const telephone = window.matchMedia('(max-width: 989px)');
    let finCompaction = 0;
    let publishTimer = 0;

    const publishHeight = () => {
      const reste = finCompaction - performance.now();
      if (reste > 0 && telephone.matches) {
        window.clearTimeout(publishTimer);
        publishTimer = window.setTimeout(publishHeight, reste + 20);
        return;
      }
      if (publishPending) return;
      publishPending = true;
      requestAnimationFrame(() => {
        publishPending = false;
        const height = Math.round(header.getBoundingClientRect().height);
        if (height === publishedHeight) return;
        publishedHeight = height;
        document.documentElement.style.setProperty('--header-height', `${height}px`);
      });
    };

    if (!header.dataset.heightBound) {
      header.dataset.heightBound = 'true';
      publishHeight();
      if ('ResizeObserver' in window) {
        new ResizeObserver(publishHeight).observe(header);
      } else {
        window.addEventListener('resize', publishHeight, { passive: true });
      }
    }

    if (!header.dataset.scrollBound) {
      header.dataset.scrollBound = 'true';
      let compact = false;
      const onScroll = () => {
        const next = window.scrollY > 60;
        if (next === compact) return;
        compact = next;
        // lmdh 12/10/2026 (lot AS, B1) : duree de la transition de l en-tete (--transition : 0,3 s), plus une marge
        finCompaction = performance.now() + 350;
        header.classList.toggle('is-compact', compact);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }

    const menuItems = Array.from(header.querySelectorAll('[data-menu-item]'));
    if (!header.dataset.menuEscapeBound) {
      header.dataset.menuEscapeBound = 'true';
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const openItem = header.querySelector('[data-menu-item].is-open');
        if (!openItem) return;
        const trigger = openItem.querySelector('[data-menu-trigger]');
        // lmdh 12/10/2026 (rapport 381, AV6) : le focus rendu a l onglet rouvrait le panneau (focusin) ; Echap le laisse ferme
        openItem.dataset.lmdhEchap = 'true';
        openItem.classList.remove('is-open');
        trigger?.setAttribute('aria-expanded', 'false');
        trigger?.focus();
      });
      document.addEventListener('pointerdown', (event) => {
        if (header.contains(event.target)) return;
        menuItems.forEach((item) => {
          if (!item.classList.contains('is-open')) return;
          item.classList.remove('is-open');
          item.querySelector('[data-menu-trigger]')?.setAttribute('aria-expanded', 'false');
        });
      });
    }

    menuItems.forEach((item) => {
      if (item.dataset.menuBound) return;
      item.dataset.menuBound = 'true';

      const panel = item.querySelector('[data-menu-panel]');
      const trigger = item.querySelector('[data-menu-trigger]');
      if (!panel || !trigger) return;

      let timer;
      const setOpen = (state) => {
        window.clearTimeout(timer);
        if (state) {
          menuItems.forEach((other) => {
            if (other === item) return;
            other.classList.remove('is-open');
            other.querySelector('[data-menu-trigger]')?.setAttribute('aria-expanded', 'false');
          });
        }
        item.classList.toggle('is-open', state);
        trigger.setAttribute('aria-expanded', String(state));
      };

      item.addEventListener('mouseenter', () => {
        delete item.dataset.lmdhEchap;
        setOpen(true);
      });
      item.addEventListener('mouseleave', () => {
        delete item.dataset.lmdhEchap;
        timer = window.setTimeout(() => setOpen(false), 140);
      });
      item.addEventListener('focusin', () => {
        if (item.dataset.lmdhEchap) return;
        setOpen(true);
      });
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) {
          delete item.dataset.lmdhEchap;
          setOpen(false);
        }
      });

      // Au clavier et au tactile, l'onglet ouvre le panneau au lieu de naviguer.
      trigger.addEventListener('click', (event) => {
        if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        if (item.classList.contains('is-open')) return;
        event.preventDefault();
        setOpen(true);
      });
    });
  };

  /* ----------------------------------------------------------------------
   * Accordeons
   *
   * Le contenu vit toujours dans le DOM (<details>), seul son affichage est
   * pilote. Ouverture exclusive par groupe, avec repli manuel pour les
   * navigateurs sans l'attribut name.
   * -------------------------------------------------------------------- */

  const setupAccordions = (root = document) => {
    const supportsExclusive = 'name' in HTMLDetailsElement.prototype;
    if (supportsExclusive) return;

    root.querySelectorAll('details[name]').forEach((details) => {
      if (details.dataset.exclusiveBound) return;
      details.dataset.exclusiveBound = 'true';

      details.addEventListener('toggle', () => {
        if (!details.open) return;
        document
          .querySelectorAll(`details[name="${details.getAttribute('name')}"][open]`)
          .forEach((sibling) => {
            if (sibling !== details) sibling.open = false;
          });
      });
    });
  };

  /* ----------------------------------------------------------------------
   * Apparition au defilement
   * -------------------------------------------------------------------- */

  const setupReveal = () => {
    if (document.documentElement.hasAttribute('data-reveal-off') || reduceMotion.matches) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    const observe = (root) => {
      root.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach((element, index) => {
        // Le decalage repart de zero a chaque groupe pour eviter des cascades
        // interminables sur les longues pages.
        element.style.setProperty('--reveal-index', String(index % 6));
        observer.observe(element);
      });
    };

    observe(document);
    document.addEventListener('shopify:section:load', (event) => observe(event.target));
  };

  /* ----------------------------------------------------------------------
   * <quantity-input>
   * -------------------------------------------------------------------- */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound === 'true') return;
      this.dataset.bound = 'true';

      this.input = this.querySelector('input');
      if (!this.input) return;

      this.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-step]');
        if (!button) return;
        event.preventDefault();

        const step = Number(button.dataset.step);
        const min = Number(this.input.min || 0);
        const max = this.input.max ? Number(this.input.max) : Infinity;
        const next = Math.min(max, Math.max(min, Number(this.input.value || 0) + step));

        if (next === Number(this.input.value)) return;
        this.input.value = String(next);
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }

  /* ----------------------------------------------------------------------
   * Jauge de l'offre 85 EUR
   *
   * Presente sur la fiche produit et le panier. Elle se recalcule a chaque
   * mise a jour du panier sans rechargement, mais son etat initial vient
   * toujours de Liquid : rien ne clignote au chargement.
   * -------------------------------------------------------------------- */

  /* lmdh, 18 septembre 2026 : meme regle que cart-offer-state.liquid — le seuil
     se mesure HORS ligne cadeau (_gift), et le cadeau n'est offert que si
     Shopify l'a remis a zero. */
  const offerStateOf = (cart) => {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    let eligible = 0;
    let hasGift = false;
    let giftFree = false;
    items.forEach((item) => {
      const isGift = Boolean(item?.properties && item.properties._gift);
      if (isGift) {
        if (!hasGift) {
          hasGift = true;
          giftFree = Number(item.final_line_price || 0) === 0;
        }
      } else if (!item?.gift_card) {
        // lmdh 08/10/2026 (critique du panier, lot Z) : une carte cadeau ne compte pas pour les 85 EUR (remise automatique)
        eligible += Number(item.final_line_price || 0);
      }
    });
    // lmdh 11/10/2026 (rapport 377, U63, lots AJ a AJ3) : meme regle que cart-offer-state.liquid. Un lot extensible ACHETE
    // tient lieu de cadeau : remise sur lui -> cadeau offert et seuil atteint ; pas encore de remise -> le seuil se compte
    // sans lui (Shopify ne compte pas l article offert dans les 85 EUR)
    const lotsCadeau = new Set([...document.querySelectorAll('[data-gift-picker] [data-gift-variant]')].map((b) => Number(b.dataset.giftVariant)));
    const lotsAchetes = items.filter((item) => !(item?.properties && item.properties._gift) && lotsCadeau.has(Number(item.variant_id)));
    const lotOffert = lotsAchetes.some((item) => [...(item.line_level_discount_allocations || []), ...(item.discounts || [])].some((d) => Number(d.amount) > 0));
    const jauge = document.querySelector('[data-offer-gauge]');
    const seuil = Number(jauge?.dataset.threshold || 0);
    // lmdh 19/10/2026 (lot BB, rapport 392) : la livraison est offerte a son propre seuil (49 EUR), le cadeau reste a 85 EUR
    const seuilLivraison = Number(jauge?.dataset.shipThreshold || 0) || seuil;
    if (lotOffert) {
      if (!hasGift) {
        hasGift = true;
        giftFree = true;
      }
      if (seuil && eligible < seuil) eligible = seuil;
    } else if (lotsAchetes.length && !hasGift) {
      hasGift = true;
      eligible -= Number(lotsAchetes[0].original_price || lotsAchetes[0].price || 0);
    }
    // lmdh 30/09/2026 (rapport 334, D2) : lot offert mis de cote sous le seuil (attribut prive)
    const parked = Boolean(cart?.attributes?._lmdh_cadeau) && !hasGift;
    // lmdh 12/10/2026 (lot AN3, R1) : la livraison offerte se compte comme Shopify (lignes a expedier, remises deduites,
    // cartes cadeaux exclues, lot achete compris) ; eligible reste le seuil du seul cadeau (cart-offer-state, champ 7)
    const livraison = items.reduce((s, item) => s + (item?.requires_shipping ? Number(item.final_line_price || 0) : 0), 0);
    const livraisonOfferte = Boolean(seuilLivraison) && livraison >= seuilLivraison;
    return { eligible, hasGift, giftFree, parked, livraison, livraisonOfferte };
  };

  const renderGauge = (gauge, totalCents, state) => {
    const threshold = Number(gauge.dataset.threshold || 0);
    if (!threshold) return;

    const unlocked = totalCents >= threshold;
    const percent = Math.min(100, Math.round((totalCents / threshold) * 100));
    const hasGift = state ? state.hasGift : gauge.dataset.hasGift === 'true';
    const giftFree = state ? state.giftFree : gauge.dataset.giftFree === 'true';
    gauge.dataset.hasGift = String(hasGift);
    gauge.dataset.giftFree = String(giftFree);
    // lmdh 19/10/2026 (lot BB) : premier palier, la livraison offerte (seuil propre, compte comme Shopify)
    const shipThreshold = Number(gauge.dataset.shipThreshold || 0);
    const livraisonOk = state ? Boolean(state.livraisonOfferte) : gauge.dataset.livraison === 'true';
    gauge.dataset.livraison = String(livraisonOk);

    const fill = gauge.querySelector('[data-gauge-fill]');
    // lmdh 28/09/2026 : passage sous le seuil -> au-dessus = moment « 85 € atteint »
    const passeLeSeuil = unlocked && gauge.dataset.unlocked === 'false';
    const largeurAvant = fill ? fill.style.width : '';
    gauge.dataset.unlocked = String(unlocked);
    if (fill) fill.style.width = `${percent}%`;
    if (passeLeSeuil) LMH.celebrerJauge(gauge, largeurAvant);
    // lmdh 29/09/2026 : jauge de la fiche produit (data-label-empty) -> panier vide = l'offre seule
    const empty = totalCents <= 0 && Boolean(gauge.dataset.labelEmpty);
    if (gauge.dataset.labelEmpty) gauge.dataset.empty = String(empty);

    const cartLine = gauge.querySelector('[data-gauge-cart]');
    if (cartLine) {
      cartLine.innerHTML = (gauge.dataset.labelCart || '').replace(
        '__amount__',
        LMH.formatMoney(totalCents)
      );
    }

    const label = gauge.querySelector('[data-gauge-label]');
    if (!label) return;

    if (unlocked) {
      const iconTpl = gauge.querySelector('[data-gauge-unlock-icon]');
      const iconHtml = iconTpl ? iconTpl.innerHTML : '';
      const unlockedLabel = giftFree
        ? gauge.dataset.labelUnlockedPerfect || gauge.dataset.labelUnlocked || ''
        : hasGift
          ? gauge.dataset.labelUnlockedChosen || gauge.dataset.labelUnlockedPlain || gauge.dataset.labelUnlocked || ''
          : gauge.dataset.labelUnlockedPlain || gauge.dataset.labelUnlocked || '';
      label.innerHTML = `${iconHtml}<span class="gauge__label-text">${unlockedLabel}</span>`;
    } else if (empty) {
      const emptyTpl = gauge.querySelector('[data-gauge-empty-icon]');
      label.innerHTML = `${emptyTpl ? emptyTpl.innerHTML : ''}<span class="gauge__label-text">${gauge.dataset.labelEmpty}</span>`;
    } else {
      let remaining = LMH.formatMoneyCourt(Math.max(0, threshold - totalCents));
      // lmdh 12/10/2026 (lot AN3, R1) : livraison deja offerte, cadeau pas encore (lot de coussins achete sous le seuil)
      // lmdh 19/10/2026 (lot BB) : meme ordre que offer-gauge.liquid ; sous le premier palier, le reste se compte vers
      // la livraison offerte, sur le montant que Shopify retient pour l expedition
      let template;
      if (hasGift && livraisonOk && gauge.dataset.labelRemainingGiftShip) template = gauge.dataset.labelRemainingGiftShip;
      else if (hasGift && gauge.dataset.labelRemainingGift) template = gauge.dataset.labelRemainingGift;
      else if (state?.parked && gauge.dataset.labelRemainingParked) template = gauge.dataset.labelRemainingParked;
      else if (livraisonOk && gauge.dataset.labelRemainingGiftShip) template = gauge.dataset.labelRemainingGiftShip;
      else if (shipThreshold && shipThreshold < threshold && gauge.dataset.labelShip) {
        template = gauge.dataset.labelShip;
        const montantLivraison = state ? Number(state.livraison || 0) : totalCents;
        remaining = LMH.formatMoneyCourt(Math.max(0, shipThreshold - montantLivraison));
      } else template = gauge.dataset.labelRemaining || '';
      const html = template.replace('__amount__', remaining);
      label.innerHTML = `<span class="gauge__label-text">${html}</span>`;
    }

    const toneHost = gauge.closest('[data-tone]');
    if (toneHost) toneHost.dataset.tone = unlocked ? 'green' : 'beige';
  };

  const setupGauges = () => {
    document.addEventListener('lmh:cart:updated', (event) => {
      const cart = event.detail?.cart;
      if (typeof cart?.total_price !== 'number') return;
      const state = offerStateOf(cart);
      document.querySelectorAll('[data-offer-gauge]').forEach((gauge) => renderGauge(gauge, state.eligible, state));
    });
  };

  /* ----------------------------------------------------------------------
   * Compteur du panier
   * -------------------------------------------------------------------- */

  const setupCartCount = () => {
    document.addEventListener('lmh:cart:updated', (event) => {
      const count = event.detail?.cart?.item_count;
      if (typeof count !== 'number') return;

      document.querySelectorAll('[data-cart-count]').forEach((element) => {
        element.textContent = String(count);
        element.toggleAttribute('hidden', count === 0);
      });

      // lmdh 28/09/2026 : le libelle lu par les lecteurs d ecran suit le compteur (il restait a « 0 article »)
      document.querySelectorAll('.header__cart').forEach((element) => {
        element.setAttribute('aria-label', `Carrito, ${count} artículo${count > 1 ? 's' : ''}`);
      });
    });
  };

  /* ----------------------------------------------------------------------
   * Defilement doux vers une ancre
   * -------------------------------------------------------------------- */

  LMH.scrollToTarget = (target) => {
    if (typeof target === 'string') target = document.querySelector(target);
    if (!target) return;

    const header = document.querySelector('[data-header]');
    const offset = (header?.getBoundingClientRect().height || 0) + 16;

    window.scrollTo({
      top: target.getBoundingClientRect().top + window.scrollY - offset,
      behavior: reduceMotion.matches ? 'auto' : 'smooth',
    });
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-scroll-to]');
    if (!link) return;
    // lmdh 07/10/2026 : sans cible sur la page, le lien suit son adresse (« Trouver ma housse » : la page du guide) ;
    // dans un tiroir ouvert, le tiroir se ferme d abord et la page defile une fois deverrouillee
    const cible = document.querySelector(link.dataset.scrollTo);
    if (!cible) return;
    event.preventDefault();
    const tiroir = link.closest('dialog[open]');
    if (tiroir) {
      tiroir.addEventListener('close', () => LMH.scrollToTarget(cible), { once: true });
      closeDialog(tiroir);
      return;
    }
    LMH.scrollToTarget(cible);
  });

  /* ----------------------------------------------------------------------
   * lmdh 30/09/2026 : pastilles actives des cartes produit (snippets/product-card).
   * Souris : le survol d une pastille montre ce coloris sur la photo de la carte, la
   * sortie de la rangee rend la photo retenue. Clic ou toucher : le coloris est retenu
   * (anneau), et les liens de la carte menent a ce coloris. Clavier : fleches gauche et
   * droite entre les pastilles d une carte (un seul arret de tabulation par carte).
   * Tout est delegue au document : les grilles rechargees par les filtres n ont rien a
   * rebrancher. La photo suivante est decodee avant l echange : pas d image blanche.
   * -------------------------------------------------------------------- */

  const setupCardSwatches = () => {
    const widths = [200, 300, 400, 600, 800];
    const imageOf = (card) => card.querySelector('.product-card__media img:not(.product-card__hover-img)');
    const srcsetOf = (src) => widths.map((w) => `${src.replace(/([?&])width=\d+/, `$1width=${w}`)} ${w}w`).join(', ');
    const paint = (card, src, srcset) => {
      const img = imageOf(card);
      if (!img || !src) return;
      if (!img.dataset.origSrc) {
        img.dataset.origSrc = img.getAttribute('src') || '';
        img.dataset.origSrcset = img.getAttribute('srcset') || '';
      }
      if (img.dataset.shown === src) return;
      img.dataset.shown = src;
      const next = new Image();
      next.sizes = img.sizes;
      next.srcset = srcset;
      next.src = src;
      img.classList.add('is-changing');
      const swap = () => {
        if (img.dataset.shown !== src) return;
        img.srcset = srcset;
        img.src = src;
        img.classList.remove('is-changing');
      };
      (next.decode ? next.decode() : Promise.resolve()).then(swap, swap);
    };
    const back = (card) => {
      card.classList.remove('is-previewing');
      const img = imageOf(card);
      if (!img || !img.dataset.origSrc) return;
      if (card.dataset.chosenSrc) paint(card, card.dataset.chosenSrc, srcsetOf(card.dataset.chosenSrc));
      else paint(card, img.dataset.origSrc, img.dataset.origSrcset);
    };
    const choose = (button) => {
      const card = button.closest('.product-card');
      if (!card) return;
      card.querySelectorAll('[data-card-swatch]').forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
      card.dataset.chosenSrc = button.dataset.src;
      card.classList.add('has-colour');
      card.classList.remove('is-previewing');
      if (button.dataset.url) card.querySelectorAll('a[href]').forEach((a) => { a.href = button.dataset.url; });
      // lmdh 30/09/2026 (rapport 334, C9) : le texte alternatif suit le coloris (il restait sur « Gris etain »)
      const photo = imageOf(card);
      if (photo && button.dataset.alt) photo.alt = button.dataset.alt;
      paint(card, button.dataset.src, srcsetOf(button.dataset.src));
    };

    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-card-swatch]');
      if (!button) return;
      event.preventDefault();
      choose(button);
    });

    document.addEventListener('pointerover', (event) => {
      if (event.pointerType !== 'mouse') return;
      const button = event.target.closest('[data-card-swatch]');
      if (!button) return;
      const card = button.closest('.product-card');
      if (!card) return;
      card.classList.add('is-previewing');
      paint(card, button.dataset.src, srcsetOf(button.dataset.src));
    });

    document.addEventListener('pointerout', (event) => {
      if (event.pointerType !== 'mouse') return;
      const list = event.target.closest('.product-card__swatches');
      if (!list || (event.relatedTarget && list.contains(event.relatedTarget))) return;
      const card = list.closest('.product-card');
      if (card) back(card);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      const button = event.target.closest('[data-card-swatch]');
      if (!button) return;
      const all = Array.from(button.closest('.product-card__swatches').querySelectorAll('[data-card-swatch]'));
      const next = all[(all.indexOf(button) + (event.key === 'ArrowRight' ? 1 : -1) + all.length) % all.length];
      if (!next || next === button) return;
      event.preventDefault();
      all.forEach((b) => b.setAttribute('tabindex', b === next ? '0' : '-1'));
      next.focus();
    });
  };

  /* ----------------------------------------------------------------------
   * Demarrage
   * -------------------------------------------------------------------- */

  customElements.define('quantity-input', QuantityInput);

  setupHeader();
  setupAccordions();
  setupReveal();
  setupCartCount();
  setupGauges();
  setupCardSwatches();

  document.addEventListener('shopify:section:load', (event) => {
    setupHeader();
    setupAccordions(event.target);
  });
})();
